import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ClearFlood,
  ClearHazard,
  DownloadCommunityInsightsReport,
  GetAssessorMapReport,
  GetBuildingPermitsReport,
  GetBuildingSketch,
  GetDocumentImageStatus,
  GetFloodMapReport,
  GetForeclosureReport,
  GetHazardReport
} from '@store/reports/reports.actions';
import * as OrdersActions from '@store/orders/orders.actions';
import {
  CancelOrderSuccess,
  GetAllCardsSuccess,
  GetCart,
  GetCartSizeSuccess,
  GetCartSuccess,
  GetHazardOfferSuccess,
  GetFloodOfferSuccess,
  GetOrdersSuccess,
  SaveCardSuccess,
  SetPromoError,
  SubmitOrderSuccess,
  GetCommunityInsightsOfferSuccess,
  GetCommunityInsightsOffer,
  UpdateCartSuccess,
} from '@store/orders/orders.actions';
import { Cart, CartInfo, CartItem, CartItemState, CartOrder, CreditCard, Order } from '@store/orders/orders.model';
import { CartUtils } from '@shared/utils/cart-utils';
import { OrderService } from '@root/orders/services/orders.services';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { catchError, filter, finalize, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Action, select, Store } from '@ngrx/store';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { RibbonService } from '@shared/services/ribbon.service';
import { ActionName } from '@shared/models/effects-names.model';
import { AppState } from '@root/app-state.module';
import { selectBuildingPermitsOffer, selectCommunityInsightsOffer, selectCart, selectCurrentStripeCard, selectDocumentImageOffer, selectAssessorMapOffer, selectForeclosureOffer, selectBuildingSketchOffer } from '@store/orders/orders.selector';
import { showGlobalAlert } from '@shared/utils/global-alerts';
import { selectSelectedPropertyIdentifier } from '@store/reports/reports.selector';
import { RootRoutes } from '@root/root-routes';
import { Router } from '@angular/router';
import { PropertyIdentifier } from '@store/search-board/search-board.model';
import { MixpanelService } from '@shared/services/mixpanel.service';

@Injectable()
export class OrdersEffects {

  constructor(private readonly orderService: OrderService,
              private readonly actions$: Actions,
              private readonly ribbonService: RibbonService,
              private readonly spinnerOverlayService: SpinnerOverlayService,
              private readonly store$: Store<AppState>,
              private readonly router: Router,
              private readonly mixpanelService: MixpanelService,
  ) {
  }

  getCart$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_CART),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.orderService.getCart()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Cart) => {
                return new GetCartSuccess(result);
              }));
        }, ActionName.GET_CART)
      );
  });

  clearAndSetNonRefundable$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_NON_REFUNDABLE),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetNonRefundable) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                 return [new ClearFlood(), new GetFloodMapReport(action.payload.propertyIdentifier)] ;
              }));
        }, ActionName.CLEAR_AND_SET_NON_REFUNDABLE)
      );
  });

  clearAndSetViewHazard$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_HAZARD),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewHazard) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                 return [new ClearHazard(), new GetHazardReport(action.payload.propertyIdentifier)] ;
              }));
        }, ActionName.CLEAR_AND_SET_VIEW_HAZARD)
      );
  });

  clearAndSetViewCommunityInsights$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_COMMUNITY_INSIGHTS),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewCommunityInsights) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                const item = action.payload.item;
                const propertyIdentifier = action.payload.propertyIdentifier;
                return [
                  new OrdersActions.GetOrders(),
                  new DownloadCommunityInsightsReport({
                    communityInsightsParameters: {
                      address: item.requestPayloadPartial,
                      apn: propertyIdentifier.apn,
                      products: [item.productId],
                      fips: propertyIdentifier.fipsCode,
                      propertyIdentifier,
                    },
                    isFreeLimit: item.isFreeLimitReport,
                  })
                ];
              }));
        }, ActionName.SET_NON_REFUNDABLE)
      );
  });

  clearAndSetViewBuildingPermits$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_BUILDING_PERMITS),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewBuildingPermits) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                return [new GetBuildingPermitsReport(action.payload.clip, action.payload.apn, action.payload.countyId)];
              }));
        }, ActionName.SET_NON_REFUNDABLE)
      );
  });

  clearAndSetViewAssessorMap$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_ASSESSOR_MAP),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewAssessorMap) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                return [new GetAssessorMapReport(action.payload.propertyIdentifier)];
              }));
        }, ActionName.SET_NON_REFUNDABLE)
      );
  });

  clearAndSetViewBuildingSketch$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_BUILDING_SKETCH),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewBuildingSketch) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                return [new GetBuildingSketch({
                  propertyIdentifier: action.payload.propertyIdentifier,
                  imageWidth: action.payload.imageWidth,
                  imageHeight: action.payload.imageHeight,
                })];
              }));
        }, ActionName.SET_NON_REFUNDABLE)
      );
  });

  clearAndSetViewForeclosure$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CLEAR_AND_SET_VIEW_FORECLOSURE),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.ClearAndSetViewForeclosure) => {
          return this.orderService.setNonRefundable(action.payload.item)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              switchMap(() => {
                return [new GetForeclosureReport(action.payload.propertyInformation)];
              }));
        }, ActionName.SET_NON_REFUNDABLE)
      );
  });

  setNonRefundable$: Observable<unknown> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.SET_NON_REFUNDABLE),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.SetNonRefundable) => {
          return this.orderService.setNonRefundable(action.payload)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Please try again later.');
                return EMPTY;
              }),
              tap(() => {
                const communityInsightsReportIds: Array<string> = ['REST', 'SCHOOL', 'ECDEM', 'TRFO'];
                if (communityInsightsReportIds.includes(action.payload.productId)) {
                  this.store$.dispatch(new OrdersActions.GetOrders);
                }
                if (action.documentImageParam) {
                  if (!action.isReportPage) {
                    this.store$.dispatch(new OrdersActions.GetOrders);
                  }
                  this.store$.dispatch(new GetDocumentImageStatus(action.documentImageParam));
                }
              }),
            );
        }, ActionName.SET_NON_REFUNDABLE)
      );
  }, { dispatch: false });

  getCartSize$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_CART_SIZE),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.orderService.getCartSize()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart size cannot be calculated right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: number) => {
                return new GetCartSizeSuccess(result);
              }));
        }, ActionName.GET_CART_SIZE)
      );
  });

  getUserCards$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_ALL_CARDS),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.orderService.getCreditCards()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Array<CreditCard>) => {
                return new GetAllCardsSuccess(result);
              }));
        }, ActionName.GET_ALL_CARDS)
      );
  });

  getOrders$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_ORDERS),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.orderService.getOrders()
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Array<Order>) => {
                return new GetOrdersSuccess(result);
              }));
        }, ActionName.GET_ORDERS)
      );
  });

  getFloodOffer$: Observable<GetFloodOfferSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_OFFER),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetOffer) => {
          return this.orderService.getFloodOffer(action.payload)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Cart) => {
                return new GetFloodOfferSuccess(result);
              }));
        }, ActionName.GET_OFFER)
      );
  });

  getAssessorMapReportOffer$: Observable<OrdersActions.GetAssessorMapReportOfferSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.GET_ASSESSOR_MAP_OFFER),
      this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetAssessorMapReportOffer) => {
        return this.orderService.getAssessorMapOffer(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
              return of(null);
            }),
            filter(Boolean),
            map((result: Cart) => new OrdersActions.GetAssessorMapReportOfferSuccess(result))
          )
      }, ActionName.GET_OFFER)
    ),
  );

  getForeclosureReportOffer$: Observable<OrdersActions.GetForeclosureReportOfferSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.GET_FORECLOSURE_OFFER),
      this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetForeclosureReportOffer) => {
        return this.orderService.getForeclosureOffer(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
              return of(null);
            }),
            filter(Boolean),
            map((result: Cart) => new OrdersActions.GetForeclosureReportOfferSuccess(result))
          )
      }, ActionName.GET_OFFER)
    ),
  );

  getBuildingSketchOffer$: Observable<OrdersActions.GetBuildingSketchOfferSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.GET_BUILDING_SKETCH_OFFER),
      this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetBuildingSketchOffer) => {
        return this.orderService.getBuildingSketchOffer(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
              return of(null);
            }),
            filter(Boolean),
            map((result: Cart) => new OrdersActions.GetBuildingSketchOfferSuccess(result))
          )
      }, ActionName.GET_OFFER)
    ),
  );

  getDocumentImageOffer$: Observable<OrdersActions.GetDocumentImageOfferSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.GET_DOCUMENT_IMAGE_OFFER),
      this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetDocumentImageOffer) => {
        return this.orderService.getDocumentImageOffer(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
              return of(null);
            }),
            filter(Boolean),
            map((result: Cart) => new OrdersActions.GetDocumentImageOfferSuccess(result))
          )
      }, ActionName.GET_OFFER)
    ),
  );

  addToCartDocumentImage$: Observable<OrdersActions.GetCart | OrdersActions.GetDocumentImageOffer> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_DOCUMENT_IMAGE),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectDocumentImageOffer)),
      ),
      map(([action, cart, documentImageOffer]: [OrdersActions.AddToCartDocumentImage, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = documentImageOffer.savedItems.filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, action.payload],
          savedItems: cart.savedItems.filter(cartItem => !(action.payload.productId === cartItem.productId &&
              action.payload.requestPayloadFull === cartItem.requestPayloadFull)),
          offer: true
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          tap(([_, propertyIdentifier]: [CartInfo, PropertyIdentifier]) => {
            this.mixpanelService.track('Cart', {
              action: 'Add To Cart',
              type: 'Document Image',
              parcelId: propertyIdentifier.parcelId,
              apn: propertyIdentifier.apn,
            });
          }),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new OrdersActions.GetDocumentImageOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => {
            this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
          })
        )
      }
    ))
  )

  addToCartAssessorMap$: Observable<OrdersActions.GetCart | OrdersActions.GetAssessorMapReportOffer> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_ASSESSOR_MAP),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectAssessorMapOffer)),
      ),
      map(([action, cart, assessorMapOffer]: [OrdersActions.AddtoCartAssessorMap, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = assessorMapOffer.savedItems.filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, action.payload],
          savedItems: cart.savedItems.filter(cartItem => !(action.payload.productId === cartItem.productId &&
              action.payload.requestPayloadFull === cartItem.requestPayloadFull)),
          offer: true
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          tap(([_, propertyIdentifier]: [CartInfo, PropertyIdentifier]) => {
            this.mixpanelService.track('Cart', {
              action: 'Add To Cart',
              type: 'Assessor Map',
              parcelId: propertyIdentifier.parcelId,
              apn: propertyIdentifier.apn,
            });
          }),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new OrdersActions.GetAssessorMapReportOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => {
            this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
          })
        )
      }
    ))
  )

  addToCartForeclosure$: Observable<OrdersActions.GetCart | OrdersActions.GetForeclosureReportOffer> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_FORECLOSURE),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectForeclosureOffer)),
      ),
      map(([action, cart, foreclosureOffer]: [OrdersActions.AddtoCartForeclosure, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = foreclosureOffer.savedItems.filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, action.payload],
          savedItems: cart.savedItems.filter(cartItem => !(action.payload.productId === cartItem.productId &&
              action.payload.requestPayloadFull === cartItem.requestPayloadFull)),
          offer: true,
          source: action.source
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          tap(([_, propertyIdentifier]: [CartInfo, PropertyIdentifier]) => {
            this.mixpanelService.track('Cart', {
              action: 'Add To Cart',
              type: 'Foreclosure Report',
              parcelId: propertyIdentifier.parcelId,
              apn: propertyIdentifier.apn,
            });
          }),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new OrdersActions.GetForeclosureReportOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => {
            this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
            if(newCart.source === 'customized') {
              this.router.navigate([RootRoutes.Orders]).then();
            }
          })
        )
      }
    ))
  )

  addToCartBuildingSketch$: Observable<OrdersActions.GetCart | OrdersActions.GetBuildingSketchOffer> = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_BUILDING_SKETCH),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectBuildingSketchOffer)),
      ),
      map(([action, cart, buildingSketchOffer]: [OrdersActions.AddtoCartBuildingSketch, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = buildingSketchOffer.savedItems.filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, action.payload],
          savedItems: cart.savedItems.filter(cartItem => !(action.payload.productId === cartItem.productId &&
              action.payload.requestPayloadFull === cartItem.requestPayloadFull)),
          offer: true,
          source: action.source
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          tap(([_, propertyIdentifier]: [CartInfo, PropertyIdentifier]) => {
            this.mixpanelService.track('Cart', {
              action: 'Add To Cart',
              type: 'Building Sketch Report',
              parcelId: propertyIdentifier.parcelId,
              apn: propertyIdentifier.apn,
            });
          }),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new OrdersActions.GetBuildingSketchOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => {
            this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
            if(newCart.source === 'customized') {
              this.router.navigate([RootRoutes.Orders]).then();
            }
          })
        )
      }
    ))
  )

  getCommunityInsightsOffer$: Observable<GetCommunityInsightsOfferSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_COMMUNITY_INSIGHTS_OFFER),
        this.spinnerOverlayService.wrapRequest((action: GetCommunityInsightsOffer) => {
          return this.orderService.getCommunityInsightsOffer(action.payload)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Cart) => {
                return new GetCommunityInsightsOfferSuccess(result)
              })
            );
        }, ActionName.GET_COMMUNITY_INSIGHTS_OFFER)
      );
  });

  getHazardOffer$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(OrdersActions.GET_HAZARD_OFFER),
      this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetHazardOffer) => {
        return this.orderService.getHazardOffer(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
              return of(null);
            }),
            filter(Boolean),
            map((result: Cart) => {
              return new GetHazardOfferSuccess(result);
            }));
      }, ActionName.GET_HAZARD_OFFER)
    ));

  addToCart: Observable<GetCart> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART),
      withLatestFrom(this.store$.pipe(select(selectCart))),
      map(([action, cart]: [OrdersActions.AddToCart, Cart]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        return {
          coupons: cart.coupons?.length ? [...cart.coupons] : null,
          items: [...cart.cartItems, action.payload],
          savedItems: cart.savedItems.filter((cartItem) => cartItem !== action.payload)
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          map((cartInfo) => {
            return new UpdateCartSuccess(cartInfo);
          }),
          map(() => {
            return new GetCart();
          }),
          catchError(() => of(void 0)),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  addToCartCommunityInsightsReports$: Observable<GetCart | GetCommunityInsightsOffer> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_COMMUNITY_INSIGHTS_REPORTS),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectCommunityInsightsOffer)),
      ),
      map(([action, cart, communityInsightsOffer]: [OrdersActions.AddtoCartCommunityInsightsReports, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = (communityInsightsOffer?.savedItems ?? [])
          .filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, ...action.payload],
          savedItems: (cart.savedItems ?? []).filter(
            (cartItem) => !action.payload.find(
              (action: CartItem) => action.productId === cartItem.productId &&
                action.requestPayloadFull === cartItem.requestPayloadFull
            )
          )
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new GetCommunityInsightsOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  removeFromCartCommunityInsightsReports$: Observable<GetCart | GetCommunityInsightsOffer> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.REMOVE_FROM_CART_COMMUNITY_INSIGHTS_REPORTS),
      withLatestFrom(this.store$.pipe(select(selectCart))),
      map(([action, cart]: [OrdersActions.RemoveFromCartCommunityInsightsReports, Cart]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const apn = CartUtils.getCartItemApn(action.payload);
        const fipsCode = CartUtils.getCartItemFipsCode(action.payload);
        return {
          coupons: cart.coupons?.length ? [...cart.coupons] : null,
          // fipsCode disambiguates cart items that share a productId+apn across different
          // counties -- without it, removal could match the wrong property's cart entry.
          items: (cart.cartItems ?? []).filter(
            (cartItem) => !CartUtils.isMatchingCartItem(cartItem, action.payload.productId, apn, fipsCode)
          ),
          savedItems: cart.savedItems ?? [],
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          // Re-reads the currently selected property (not the removed offer's own apn) after
          // updateCart resolves, mirroring addToCartCommunityInsightsReports$'s mitigation so a
          // property navigation mid-request doesn't refresh the wrong property's CI offer.
          switchMap(([_, propertyIdentifier]: [CartInfo, PropertyIdentifier]) =>
            [new GetCart(), new GetCommunityInsightsOffer(propertyIdentifier.apn)]),
          catchError(() => of(void 0)),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  applyCoupon: Observable<UpdateCartSuccess | SetPromoError | GetCart> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.APPLY_COUPON),
      withLatestFrom(this.store$.pipe(select(selectCart))),
      map(([action, cart]: [OrdersActions.ApplyCoupon, Cart]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        return {
          coupons: action.payload === '' ? null : [action.payload],
          items: [...cart.cartItems],
          savedItems: [...cart.savedItems]
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          map((cartInfo) => {
            return new UpdateCartSuccess(cartInfo);
          }),
          map(() => {
            return new GetCart();
          }),
          catchError(() => {
            this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Check the code and try again, or this promo expired.');
            return of(new SetPromoError());
          }),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  saveForLater: Observable<GetCart> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.SAVE_FOR_LATER),
      withLatestFrom(this.store$.pipe(select(selectCart))),
      map(([action, cart]: [OrdersActions.AddToCart, Cart]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        return {
          coupons: cart.coupons?.length ? [...cart.coupons] : null,
          items: cart.cartItems.filter((cartItem) =>
            cartItem.productId !== action.payload.productId
            || cartItem.requestPayloadFull !== action.payload.requestPayloadFull),
          savedItems: [...cart.savedItems, action.payload]
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          map((cartInfo) => {
            return new UpdateCartSuccess(cartInfo);
          }),
          map(() => {
            return new GetCart();
          }),
          catchError(() => of(void 0)),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  removeCartItem: Observable<GetCart> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.REMOVE_FROM_CART),
      withLatestFrom(this.store$.pipe(select(selectCart))),
      map(([action, cart]: [OrdersActions.RemoveFromCart, Cart]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        return {
          coupons: cart.coupons?.length ? [...cart.coupons] : null,
          items: action.payload.type === 'cart'
            ? cart.cartItems.filter((cartItem) => cartItem !== action.payload.element)
            : cart.cartItems,
          savedItems: action.payload.type === 'savedForLater'
            ? cart.savedItems.filter((cartItem) => cartItem !== action.payload.element)
            : cart.savedItems,
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          map((cartInfo) => {
            return new UpdateCartSuccess(cartInfo);
          }),
          map(() => {
            return new GetCart();
          }),
          catchError(() => of(void 0)),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART))
        );
      }),
    );
  });

  updateCart$: Observable<OrdersActions.UpdateCartSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.UPDATE_CART),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.UpdateCart) => {
          return this.orderService.updateCart(action.payload).pipe(
            map((cartInfo) => {
              return new UpdateCartSuccess(cartInfo);
            }),
            catchError(() => of(void 0)),
            filter<OrdersActions.UpdateCartSuccess>(Boolean)
          );
        }, ActionName.UPDATE_CART)
      );
  });

  createOrder$: Observable<OrdersActions.SubmitOrderSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.SUBMIT_ORDER),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.SubmitOrder) => {
          return this.orderService.createOrder(action.payload)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, error.error);
                return throwError(error);
              }),
              filter(Boolean),
              map((result: Order) => {
                return new SubmitOrderSuccess(result);
              }));
        }, ActionName.SUBMIT_ORDER)
      );
  });

  cancelOrder$: Observable<Action> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.CANCEL_ORDER),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.CancelOrder) => {
          return this.orderService.cancelOrder(action.payload)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Array<Order>) => {
                showGlobalAlert(
                  'Your return has been submitted. The estimated refund timing is 7 business days.',
                  'alert-success',
                  'fa-circle-check'
                );
                return new CancelOrderSuccess(result);
              }));
        }, ActionName.CANCEL_ORDER)
      );
  });

  saveCard$: Observable<SaveCardSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.SAVE_CARD),
        withLatestFrom(this.store$.pipe(select(selectCurrentStripeCard))),
        switchMap(([taxAddress, card]: any) => {
          this.spinnerOverlayService.showSpinner(ActionName.SAVE_CARD);
          const {token, ...address} = taxAddress.payload;
          const cardForSave: CreditCard = {
            brand: card.brand,
            expiryMonth: card.exp_month || card.expiryMonth,
            expiryYear: card.exp_year || card.expiryYear,
            lastFourDigits: card.last4 || card.lastFourDigits,
            email: taxAddress.email,
            name: card.name,
            token: card.token || token,
            taxAddress: JSON.stringify({
              address1: taxAddress.payload.address,
              city: taxAddress.payload.city,
              state: taxAddress.payload.state,
              zipCode: taxAddress.payload.zip
            }),
            ...address
          };

          return this.orderService.addCardAndSubmitOrder(cardForSave)
            .pipe(finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.SAVE_CARD)),
              catchError((err) => {
                if (err.status == HttpStatusCode.BadRequest) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, err.error);
                } else if (err.status == HttpStatusCode.FailedDependency) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Address not found.');
                } else if (err.status == HttpStatusCode.InternalServerError) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, err.error);
                } else if (err.status == HttpStatusCode.ServiceUnavailable) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Unable to validate address. Please try again in a few minutes.');
                } else if (err.error && err.error.errorMessage.indexOf('must be less than or equal to 2040') > -1) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Please check the expire date to make sure it is less than or equal to 2040.');
                } else {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Please try again later.');
                }
                return of(null);
              }),
              filter(Boolean),
              map((result: CartOrder) => {
                return new SaveCardSuccess(result);
              })
            );
        })
      );
  });

  getBuildingPermitsOffer$: Observable<OrdersActions.GetBuildingPermitsOfferSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(OrdersActions.GET_BUILDING_PERMITS_OFFER),
        this.spinnerOverlayService.wrapRequest((action: OrdersActions.GetBuildingPermitsOffer) => {
          return this.orderService.getBuildingPermitsOffer(action.payload)
            .pipe(
              catchError(() => {
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'The cart cannot be loaded right now. Please try again later.');
                return of(null);
              }),
              filter(Boolean),
              map((result: Cart) => {
                return new OrdersActions.GetBuildingPermitsOfferSuccess(result)
              })
            );
        }, ActionName.GET_BUILDING_PERMITS_OFFER)
      );
  });

  addToCartBuildingPermitsReports$: Observable<GetCart | OrdersActions.GetBuildingPermitsOffer> = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrdersActions.ADD_TO_CART_BUILDING_PERMITS_REPORTS),
      withLatestFrom(
        this.store$.pipe(select(selectCart)),
        this.store$.pipe(select(selectBuildingPermitsOffer)),
      ),
      map(([action, cart, buildingPermitsOffer]: [OrdersActions.AddtoCartBuildingPermitsReports, Cart, CartInfo]) => {
        this.spinnerOverlayService.showSpinner(ActionName.UPDATE_CART);
        const inCartItems: Array<CartItem> = buildingPermitsOffer.savedItems.filter((item: CartItem) => item.state === CartItemState.IN_CART);
        return {
          items: [...inCartItems, action.payload],
          savedItems: cart.savedItems.filter(cartItem => !(action.payload.productId === cartItem.productId &&
              action.payload.requestPayloadFull === cartItem.requestPayloadFull)),
          offer: true,
          source: action.source
        };
      }),
      switchMap((newCart) => {
        return this.orderService.updateCart(newCart).pipe(
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          switchMap(([_, propertyIdentifier]) => {
            return [new GetCart(), new OrdersActions.GetBuildingPermitsOffer(propertyIdentifier.apn)];
          }),
          catchError(() => of(void 0)),
          finalize(() => {
            this.spinnerOverlayService.hideSpinner(ActionName.UPDATE_CART);
            if(newCart.source === 'customized') {
              this.router.navigate([RootRoutes.Orders]).then();
            }
          })
        );
      }),
    );
  });
}
